import React, { useRef, useEffect, useState } from "react";
import {
  VectorMap,
  Layer,
  Legend,
  Source,
  Tooltip,
} from "devextreme-react/vector-map";
import * as mapsData from "devextreme-dist/js/vectormap-data/world.js";
import ChartCard from "components/common/pyramid/ChartCard";

const BreakdownByCountryMap = ({ data, palette }) => {
  const chartRef = useRef(null);
  const [transformedData, setTransformedData] = useState(null);
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [renderKey, setRenderKey] = useState(0);

  const toTitleCase = (str) => {
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const formatCurrency = (value) => {
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
      notation: "compact",
      compactDisplay: "short",
    });
    return formatter.format(value);
  };

  useEffect(() => {
    if (data && data.length > 0) {
      const transformed = data.reduce((acc, item) => {
        acc[toTitleCase(item.name)] = {
          bucket1: item.bucket1,
          bucket2: item.bucket2,
          total: item.total,
        };
        return acc;
      }, {});

      setTransformedData(transformed);
    }
  }, [data]);

  useEffect(() => {
    // Force a rerender of the underlying vector map so color grouping recalculates
    setRenderKey((prev) => prev + 1);
  }, [transformedData]);

  const handleTooltipShowing = (e) => {
    if (e.target) {
      const countryName = e.target.attribute("name");
      if (hoveredCountry !== countryName) {
        setHoveredCountry(countryName);
      }
    }
  };

  if (!transformedData) {
    return <div>Loading...</div>;
  }

  const colorGroups = [
    0, 100000, 500000, 1000000, 10000000, 50000000, 100000000, 1000000000,
  ];

  const mapPalette = [
    "#EAEAEA",
    "#F4E5E4",
    "#E5C0BB",
    "#D69B93",
    "#C77667",
    "#B34B3D",
    "#8B2F23",
  ];

  return (
    <ChartCard title="Breakdown by Country Map" chartRef={chartRef}>
      <div className="p-4">
        <VectorMap
          key={renderKey}
          controlBar={{
            enabled: false,
          }}
          onTooltipShowing={handleTooltipShowing}
          ref={chartRef}
        >
          <Layer
            dataSource={mapsData.world}
            name="areas"
            colorGroups={colorGroups}
            palette={mapPalette}
            colorGroupingField="total"
            customize={(elements) => {
              elements.forEach((element) => {
                const country = transformedData[element.attribute("name")];
                if (country && country.total) {
                  element.attribute("total", country.total);
                } else {
                  element.applySettings({
                    color: "#777777",
                  });
                }
              });
            }}
          ></Layer>
          <Tooltip
            enabled={true}
            customizeTooltip={(info) => {
              const countryName = info.attribute("name");
              const countryECLData = transformedData[countryName];

              const formatter = new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "EUR",
                maximumFractionDigits: 0,
              });

              if (!countryECLData || countryECLData.total === 0) {
                return {
                  html: `
                    <div>
                      <h4 style="margin: 0 0 8px 0;">${countryName}</h4>
                      <div>ECL : 0 Euro</div>
                    </div>
                  `,
                };
              }

              const total = countryECLData.bucket1 + countryECLData.bucket2;
              const bucket1Percent =
                total > 0 ? (countryECLData.bucket1 / total) * 100 : 0;
              const bucket2Percent =
                total > 0 ? (countryECLData.bucket2 / total) * 100 : 0;

              return {
                html: `
                  <div>
                    <h4 style="margin: 0 0 8px 0;">${countryName}</h4>
                    <div style="margin-bottom: 15px;">ECL : ${formatter.format(
                      countryECLData.total
                    )}</div>
                    
                    <div style="display: flex; align-items: center; margin-bottom: 10px;">
                      <div style="width: 100px; height: 100px; border-radius: 50%; position: relative; margin-right: 20px; background: conic-gradient(#ADB9CA 0% ${bucket1Percent}%, #333F50 ${bucket1Percent}% 100%);">
                        <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); text-align: center; color: white; font-size: 10px; font-weight: bold;">
                        </div>
                      </div>
                      <div>
                        <div style="display: flex; align-items: center; margin-bottom: 5px;">
                          <div style="width: 12px; height: 12px; background: #ADB9CA; margin-right: 8px;"></div>
                          <span style="font-size: 12px;">Bucket 1: ${formatter.format(
                            countryECLData.bucket1
                          )}</span>
                        </div>
                        <div style="display: flex; align-items: center;">
                          <div style="width: 12px; height: 12px; background: #333F50; margin-right: 8px;"></div>
                          <span style="font-size: 12px;">Bucket 2: ${formatter.format(
                            countryECLData.bucket2
                          )}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                `,
              };
            }}
          />
          <Legend
            customizeText={(itemInfo) => {
              if (itemInfo.index === 0) {
                return `< ${formatCurrency(colorGroups[1])}`;
              } else if (itemInfo.index === colorGroups.length - 2) {
                return `> ${formatCurrency(
                  colorGroups[colorGroups.length - 2]
                )}`;
              } else {
                return `${formatCurrency(
                  colorGroups[itemInfo.index]
                )} - ${formatCurrency(colorGroups[itemInfo.index + 1])}`;
              }
            }}
            markerSize={12}
            horizontalAlignment="center"
            verticalAlignment="bottom"
            columnCount={4}
            font={{ size: 12, family: "Segoe UI" }}
            paddingLeftRight={10}
            paddingTopBottom={10}
            title={{
              text: "ECL by country (EUR)",
              font: { size: 14, weight: 500, family: "Segoe UI" },
              margin: { bottom: 10 },
            }}
          >
            <Source layer="areas" grouping="color"></Source>
          </Legend>
        </VectorMap>
      </div>
    </ChartCard>
  );
};

export default BreakdownByCountryMap;
